/**
 * Card 2508 — a record's DECLARED columns and DECLARED actions, on its own page (the approved
 * prototype's screen 2, the client page's review card).
 *
 * <p>Both halves are the declaration's. The values are `status_view.list_fields` resolved for this
 * record (the same `{ type, value }` cells the board draws, through the same renderer). The buttons
 * are `status_view.actions`: a `signal_now` action (Send re-confirm links now) asks once and fires;
 * a `set_field` action (Change review date) opens a dialog for the new value and a reason, which is
 * recorded against the staff member's name. Each button is drawn ONLY when the engine said this
 * viewer may take it (`allowed`) and the record can take it now (`available`) — a role-gated or
 * out-of-state action is absent, not disabled — and a refusal that
 * still comes back (403) is said in words inside the dialog.</p>
 *
 * <p>What the page posts is the declared action's NAME, the value and the reason. Which field it
 * writes, which flag it clears and what it fires are the declaration's, on the engine side.</p>
 */
import { useCallback, useRef, useState } from "react";
import { Button, Input, KV, Textarea, Toast } from "@ardept/design-system";
import { ApiError } from "../api/fetcher.js";
import { describeActionError, setJobField, signalJobNow, useJobAction } from "../api/portalQueries.js";
import type { ActionErrorBody, JobStatusDeclaredActionRef, JobStatusDetailResponse } from "../api/types.js";
import { FieldCell, columnsFor } from "./boardFields.js";
import { ConfirmDialog, ResolveDialog } from "./fileActions.js";

/** A declared action reference, split into its kind and its declared name. */
export interface DeclaredAction {
  kind: "set_field" | "signal_now";
  name: string;
  ref: JobStatusDeclaredActionRef;
}

/** `"set_field:change_review_date"` → `{ kind: "set_field", name: "change_review_date" }`; null for anything else. */
export function parseDeclaredAction(ref: JobStatusDeclaredActionRef): DeclaredAction | null {
  const at = ref.action.indexOf(":");
  if (at <= 0) return null;
  const kind = ref.action.slice(0, at);
  const name = ref.action.slice(at + 1);
  if (name.trim() === "") return null;
  if (kind !== "set_field" && kind !== "signal_now") return null;
  return { kind, name, ref };
}

/**
 * The actions this viewer may take on this record NOW, in the order the engine sent them: the role
 * check passed (`allowed`) and the record's current state can take it (`available` — a signal the
 * state does not route would only come back 409 `not_available_in_state`).
 */
export function drawableActions(detail: JobStatusDetailResponse): DeclaredAction[] {
  return (detail.actions ?? [])
    .filter((ref) => ref.allowed && ref.available)
    .map(parseDeclaredAction)
    .filter((action): action is DeclaredAction => action !== null);
}

/** A set-field or signal refusal, in words: the shared staff-action wording plus this dialog's own two. */
export function describeDeclaredError(error: unknown): ReturnType<typeof describeActionError> {
  if (error instanceof ApiError && error.status === 400) {
    let body: ActionErrorBody = {};
    try {
      body = JSON.parse(error.bodyText) as ActionErrorBody;
    } catch {
      body = {};
    }
    const code = body.error_code ?? body.error;
    if (code === "missing_reason") return { kind: "rejected", message: "Say why you are making this change." };
    if (code === "invalid_value" || code === "invalid_date") {
      return { kind: "rejected", message: "That value was not accepted. Check it and try again." };
    }
  }
  return describeActionError(error);
}

/** Today as `YYYY-MM-DD`, the date input's own format. */
function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/** The set-field dialog: the new value (a date, unless declared text) and a reason. */
export function SetFieldDialog({
  action,
  viewer,
  pending,
  error,
  onCancel,
  onSave,
}: {
  action: DeclaredAction;
  viewer: string | null;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onSave: (value: string, reason: string | null) => void;
}): JSX.Element {
  const isText = action.ref.type === "text";
  // The engine sends `reason_required` only when the action declares it (omitted otherwise).
  const reasonRequired = action.ref.reason_required === true;
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");
  const ready = value.trim() !== "" && (!reasonRequired || reason.trim() !== "");
  return (
    <ResolveDialog
      title={action.ref.label}
      description={
        reasonRequired
          ? "Your reason is kept in the history, against your name."
          : "The change is kept in the history, against your name."
      }
      viewer={viewer}
      primaryLabel="Save"
      pending={pending}
      canSubmit={ready}
      error={error}
      onCancel={onCancel}
      onSubmit={() => onSave(value.trim(), reason.trim() === "" ? null : reason.trim())}
    >
      <label className="lt-field">
        <span className="lt-label">{isText ? "New value" : "New date"}</span>
        {isText ? (
          <Input
            variant="text"
            autoFocus
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
        ) : (
          <Input
            variant="date"
            type="date"
            autoFocus
            min={todayIso()}
            value={value}
            onChange={(event) => setValue(event.target.value)}
          />
        )}
      </label>
      <label className="lt-field">
        <span className="lt-label">
          Reason {!reasonRequired && <span className="lt-optional">(optional)</span>}
        </span>
        <Textarea
          rows={3}
          required={reasonRequired}
          placeholder="For example, the client is away until November"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </label>
    </ResolveDialog>
  );
}

type Open = { action: DeclaredAction } | null;

/**
 * The record's summary card: its declared values and the declared actions this viewer may take.
 * Renders nothing when the record declares neither.
 */
export function RecordSummary({
  detail,
  tenantId,
  jobId,
  viewer,
}: {
  detail: JobStatusDetailResponse;
  tenantId: string | null;
  jobId: string | undefined;
  viewer: string | null;
}): JSX.Element | null {
  const [open, setOpen] = useState<Open>(null);
  const [dialogError, setDialogError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ id: number; message: string; tone: "success" | "error" } | null>(null);
  const toastId = useRef(0);
  const setField = useJobAction(tenantId, jobId, setJobField);
  const signal = useJobAction(tenantId, jobId, signalJobNow);

  const say = useCallback((message: string, tone: "success" | "error"): void => {
    toastId.current += 1;
    setToast({ id: toastId.current, message, tone });
  }, []);
  const dismiss = useCallback((): void => setToast(null), []);
  const close = (): void => {
    setOpen(null);
    setDialogError(null);
  };

  /** A refusal: one the staffer can fix (or "only X can do this") stays in the dialog. */
  const failed = (error: unknown): void => {
    const failure = describeDeclaredError(error);
    if (failure.kind === "rejected" || failure.kind === "role_required" || failure.kind === "failed") {
      setDialogError(failure.message);
      return;
    }
    say(failure.message, "error");
    close();
  };

  const columns = columnsFor([{ fields: detail.fields }]);
  const actions = drawableActions(detail);
  if (columns.length === 0 && actions.length === 0) return null;
  const now = Date.now();
  const firstSignal = actions.find((action) => action.kind === "signal_now");

  return (
    <section className="fd-block fd-block--panel rs-card" aria-labelledby="rs-summary-h">
      <div className="fd-block__head">
        <h2 id="rs-summary-h" className="fd-block__title">
          Summary
        </h2>
      </div>
      {columns.length > 0 && (
        <div className="rs-values">
          {columns.map((column) => (
            <KV
              key={column.name}
              label={column.label}
              value={<FieldCell fields={detail.fields} column={column} now={now} />}
            />
          ))}
        </div>
      )}
      {actions.length > 0 && (
        <div className="rs-actions">
          {actions.map((action) => (
            <Button
              key={action.ref.action}
              variant={action === firstSignal ? "primary" : "ghost"}
              onClick={() => {
                setDialogError(null);
                setOpen({ action });
              }}
            >
              {action.ref.label}
            </Button>
          ))}
        </div>
      )}

      {open !== null && open.action.kind === "set_field" && (
        <SetFieldDialog
          action={open.action}
          viewer={viewer}
          pending={setField.isPending}
          error={dialogError}
          onCancel={close}
          onSave={(value, reason) => {
            setDialogError(null);
            setField.mutate(
              { action: open.action.name, value, ...(reason === null ? {} : { reason }) },
              {
                onSuccess: () => {
                  say(`Saved. "${open.action.ref.label}" is recorded in the history.`, "success");
                  close();
                },
                onError: failed,
              },
            );
          }}
        />
      )}

      {open !== null && open.action.kind === "signal_now" && (
        <ConfirmDialog
          title={open.action.ref.label}
          body="This happens straight away, and it is recorded in the history against your name."
          confirmLabel={open.action.ref.label}
          pending={signal.isPending}
          error={dialogError}
          onCancel={close}
          onConfirm={() => {
            setDialogError(null);
            signal.mutate(
              { action: open.action.name },
              {
                onSuccess: () => {
                  say(`Done. "${open.action.ref.label}" is recorded in the history.`, "success");
                  close();
                },
                onError: failed,
              },
            );
          }}
        />
      )}

      {toast !== null && <Toast key={toast.id} message={toast.message} tone={toast.tone} onDismiss={dismiss} />}
    </section>
  );
}
